import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResearchMatrixState } from './state/useResearchMatrixState';
import { usePublicResearchData } from './usePublicResearchData';
import { useSelectedResearchData } from './data/useSelectedResearchData';
import { useResearchMatrixFavorites } from './actions/useResearchMatrixFavorites';
import { useResearchItemConverter } from './utils/useResearchItemConverter';

/**
 * Public research matrix logic hook
 * Uses the public research library data source
 */
export const usePublicResearchMatrixLogic = () => {
  const queryClient = useQueryClient();
  
  // State management
  const { state, actions } = useResearchMatrixState();

  // Use public research data
  const listDataResult = usePublicResearchData({
    showFavorites: state.showFavorites,
    showOnlyUserResearch: state.showOnlyUserResearch,
  });

  const detailsDataResult = useSelectedResearchData({
    selectedResearchId: state.selectedResearchId,
  });

  // Favorite operations
  const { handleToggleFavorite } = useResearchMatrixFavorites({
    allResearch: listDataResult.allResearch,
  });

  // Research item conversion
  const { selectedResearch, convertToResearchItem } = useResearchItemConverter({
    selectedResearchId: state.selectedResearchId,
    selectedResearchDetails: detailsDataResult.selectedResearchDetails,
    allResearch: listDataResult.allResearch,
  });

  // Update current index when research is selected
  useEffect(() => {
    if (state.selectedResearchId && listDataResult.research.length > 0) {
      const index = listDataResult.research.findIndex(
        research => research.id === state.selectedResearchId
      );
      if (index !== -1 && index !== state.currentResearchIndex) {
        actions.setCurrentResearchIndex(index);
      }
    }
  }, [state.selectedResearchId, listDataResult.research, state.currentResearchIndex, actions]);

  // Smart pre-fetching system for seamless navigation
  useEffect(() => {
    if (state.currentResearchIndex >= 0 && listDataResult.research.length > 0) {
      const currentIndex = state.currentResearchIndex;
      const allResearch = listDataResult.research;
      
      // Pre-fetch adjacent items for instant navigation
      const itemsToPreload = [];
      
      // Previous item
      if (currentIndex > 0) {
        itemsToPreload.push(allResearch[currentIndex - 1]?.id);
      }
      
      // Next item
      if (currentIndex < allResearch.length - 1) {
        itemsToPreload.push(allResearch[currentIndex + 1]?.id);
      }
      
      // Aggressive pre-loading: load 2 items ahead and behind for ultra-smooth navigation
      if (currentIndex > 1) {
        itemsToPreload.push(allResearch[currentIndex - 2]?.id);
      }
      if (currentIndex < allResearch.length - 2) {
        itemsToPreload.push(allResearch[currentIndex + 2]?.id);
      }
      
      // Pre-fetch all adjacent items
      itemsToPreload.filter(Boolean).forEach(researchId => {
        queryClient.prefetchQuery({
          queryKey: ['research-item-details', researchId],
          staleTime: 10 * 60 * 1000, // 10 minutes
        });
      });
    }
  }, [state.currentResearchIndex, listDataResult.research, queryClient]);

  // Navigation calculations
  const hasPrevious = state.currentResearchIndex > 0;
  const hasNext = state.currentResearchIndex < listDataResult.research.length - 1;
  const isNearEnd = state.currentResearchIndex >= listDataResult.research.length - 3;

  // Enhanced navigation handlers with optimistic updates
  const handlePreviousResearch = () => {
    if (hasPrevious) {
      actions.handlePreviousResearch(listDataResult.research);
    }
  };

  const handleNextResearch = () => {
    if (hasNext) {
      actions.handleNextResearch(listDataResult.research);
    } else if (isNearEnd && listDataResult.hasNextPage && !listDataResult.isFetchingNextPage) {
      // Load more when near the end
      listDataResult.fetchNextPage();
    }
  };

  return {
    // State
    showOnlyUserResearch: state.showOnlyUserResearch,
    showFavorites: state.showFavorites,
    selectedResearch,
    isLightboxOpen: state.isLightboxOpen,
    allResearch: listDataResult.research,
    
    // Loading states
    isLoading: listDataResult.isLoading,
    error: listDataResult.error,
    hasNextPage: listDataResult.hasNextPage,
    isFetchingNextPage: listDataResult.isFetchingNextPage,
    isLoadingDetails: detailsDataResult.isLoading,
    selectedResearchDetails: detailsDataResult.selectedResearchDetails,
    
    // Actions
    handleFilterChange: actions.handleFilterChange,
    handleResearchClick: actions.handleResearchClick,
    handleCloseLightbox: actions.handleCloseLightbox,
    handleToggleFavorite,
    fetchNextPage: listDataResult.fetchNextPage,
    
    // Navigation
    hasPrevious,
    hasNext,
    handlePreviousResearch,
    handleNextResearch,
    
    // Utils
    convertToResearchItem,
  };
};